"use client";
import { useState, useEffect, useRef, useMemo } from "react";
import SignupSlider from "../../Components/Signupslider/index";
import { Link, useNavigate } from "react-router-dom";
import { handleAdminApiCall, handleApiCall } from "../../apis";
import { endpoint } from "../../utils/endpoints";
import { toast } from "react-toastify";
import indianStatesAndCities from "../../utils/indianStatesAndCities.json";

const normalizeFileList = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value.filter((f) => f instanceof File);
  }
  if (value instanceof File) return [value];
  return [];
};

const safeStr = (v) => (v == null ? "" : String(v)).trim();

/** Resolve API service row from dropdown label (handles name/title/case mismatches). */
const findServiceForBusinessOption = (servicesList, option) => {
  const list = Array.isArray(servicesList) ? servicesList : [];
  const opt = safeStr(option);
  if (!opt) return null;
  const lower = opt.toLowerCase();
  let row = list.find((s) => safeStr(s?.name) === opt);
  if (row) return row;
  row = list.find((s) => safeStr(s?.name).toLowerCase() === lower);
  if (row) return row;
  row = list.find((s) => safeStr(s?.title).toLowerCase() === lower);
  if (row) return row;
  return list.find((s) => safeStr(s?.service_name).toLowerCase() === lower);
};

/** Fill in service_id from the services list when it was missing (e.g. API loaded late). */
const hydrateServiceIdsOnDetails = (businessDetails, typeLabels, servicesList) => {
  const details = Array.isArray(businessDetails) ? businessDetails : [];
  const labels = Array.isArray(typeLabels) ? typeLabels : [];
  return details.map((bt, i) => {
    if (bt?.service_id) return bt;
    const label = safeStr(bt?.service_name) || safeStr(labels[i]);
    if (!label) return bt;
    const resolved = findServiceForBusinessOption(servicesList, label);
    const id = resolved?._id || resolved?.id;
    return id ? { ...bt, service_id: id } : bt;
  });
};

const Signup = () => {
  const [isOpens, setIsOpens] = useState(false);
  const [isRegisterTypeOpen, setIsRegisterTypeOpen] = useState(false);
  const registerTypeDropdownRef = useRef(null);
  const businessTypeDropdownRef = useRef(null);
  const scrollableContainerRef = useRef(null);
  const navigate = useNavigate();
  const [eError, setEError] = useState({});
  const [businessTypeOptions, setBusinessTypeOptions] = useState([]);
  const [services, setServices] = useState([]);
  const [loadingServices, setLoadingServices] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentBusinessIndex, setCurrentBusinessIndex] = useState(0);
  const [completedBusinesses, setCompletedBusinesses] = useState([]);
  const [currentStep, setCurrentStep] = useState(1); // 1 = Vendor details, 2 = Business details

  const [Signupdata, setSignUpdata] = useState({
    vendorName: "",
    vendorPhone: "",
    vendorEmail: "",
    Business_type: [],
    Register_type: "",
    businessDetails: [],
  });

  const [errors, setErrors] = useState({});
  const [vendorErrors, setVendorErrors] = useState({});
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);

  const registerTypeOptions = ["Rent", "Service", "Shop"];

  useEffect(() => {
    const fetchBusinessTypes = async () => {
      setLoadingServices(true);
      try {
        const response = await handleAdminApiCall("get", endpoint.getServices);
        if (response?.data?.code === 200) {
          const servicesList = response?.data?.data || [];
          setServices(servicesList);
          const serviceNames = servicesList
            .map((service) =>
              safeStr(service?.name) ||
              safeStr(service?.title) ||
              safeStr(service?.service_name)
            )
            .filter(Boolean);
          setBusinessTypeOptions(serviceNames);
        } else {
          toast.error(
            response?.response?.data?.errorMessage ||
              "Failed to load business types"
          );
        }
      } catch (error) {
        console.error("Error fetching business types:", error);
        toast.error("Failed to load business types");
      } finally {
        setLoadingServices(false);
      }
    };

    fetchBusinessTypes();
  }, []);

  /* When services finish loading, attach service_id to rows that were picked before the list was ready. */
  useEffect(() => {
    if (!Array.isArray(services) || services.length === 0) return;
    setSignUpdata((prev) => {
      if (prev.Register_type !== "Service" || !prev.businessDetails?.length) {
        return prev;
      }
      const next = hydrateServiceIdsOnDetails(
        prev.businessDetails,
        prev.Business_type,
        services
      );
      const same = next.every(
        (bt, i) =>
          (bt?.service_id || "") === (prev.businessDetails[i]?.service_id || "")
      );
      if (same) return prev;
      return { ...prev, businessDetails: next };
    });
  }, [services]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpens && !event.target.closest(".business-type-dropdown")) {
        setIsOpens(false);
      }
      if (
        isRegisterTypeOpen &&
        !event.target.closest(".register-type-dropdown")
      ) {
        setIsRegisterTypeOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpens, isRegisterTypeOpen]);

  useEffect(() => {
    if (Signupdata.Register_type === "Service") {
      setCurrentBusinessIndex(0);
      setCompletedBusinesses([]);
    }
  }, [Signupdata.Business_type]);

  // Scroll dropdown into view when it opens
  useEffect(() => {
    if (
      isRegisterTypeOpen &&
      registerTypeDropdownRef.current &&
      scrollableContainerRef.current
    ) {
      setTimeout(() => {
        const dropdownElement = registerTypeDropdownRef.current;
        const scrollContainer = scrollableContainerRef.current;

        const dropdownRect = dropdownElement.getBoundingClientRect();
        const containerRect = scrollContainer.getBoundingClientRect();
        const scrollTop = scrollContainer.scrollTop;

        // Calculate the position of dropdown relative to container
        const dropdownTop = dropdownRect.top - containerRect.top + scrollTop;
        const dropdownBottom = dropdownTop + dropdownElement.offsetHeight + 200; // Add extra space for dropdown menu

        // Check if dropdown is visible in viewport
        const containerHeight = scrollContainer.clientHeight;
        const currentScrollTop = scrollContainer.scrollTop;

        if (dropdownBottom > currentScrollTop + containerHeight) {
          // Scroll to show the dropdown
          scrollContainer.scrollTo({
            top: dropdownBottom - containerHeight + 20,
            behavior: "smooth",
          });
        } else if (dropdownTop < currentScrollTop) {
          // Scroll up if dropdown is above viewport
          scrollContainer.scrollTo({
            top: dropdownTop - 20,
            behavior: "smooth",
          });
        }
      }, 150);
    }
  }, [isRegisterTypeOpen]);

  useEffect(() => {
    if (
      isOpens &&
      businessTypeDropdownRef.current &&
      scrollableContainerRef.current
    ) {
      setTimeout(() => {
        const dropdownElement = businessTypeDropdownRef.current;
        const scrollContainer = scrollableContainerRef.current;

        const dropdownRect = dropdownElement.getBoundingClientRect();
        const containerRect = scrollContainer.getBoundingClientRect();
        const scrollTop = scrollContainer.scrollTop;

        // Calculate the position of dropdown relative to container
        const dropdownTop = dropdownRect.top - containerRect.top + scrollTop;
        const dropdownBottom = dropdownTop + dropdownElement.offsetHeight + 200; // Add extra space for dropdown menu

        // Check if dropdown is visible in viewport
        const containerHeight = scrollContainer.clientHeight;
        const currentScrollTop = scrollContainer.scrollTop;

        if (dropdownBottom > currentScrollTop + containerHeight) {
          // Scroll to show the dropdown
          scrollContainer.scrollTo({
            top: dropdownBottom - containerHeight + 20,
            behavior: "smooth",
          });
        } else if (dropdownTop < currentScrollTop) {
          // Scroll up if dropdown is above viewport
          scrollContainer.scrollTo({
            top: dropdownTop - 20,
            behavior: "smooth",
          });
        }
      }, 150);
    }
  }, [isOpens]);

  const handleBusinessTypeCheckbox = (option) => {
    setSignUpdata((prev) => {
      const isSelected = prev.Business_type.includes(option);
      let updatedBusinessType, updatedBusinessDetails;

      if (isSelected) {
        const indexToRemove = prev.Business_type.indexOf(option);
        updatedBusinessType = prev.Business_type.filter(
          (item) => item !== option
        );
        updatedBusinessDetails = prev.businessDetails.filter(
          (_, index) => index !== indexToRemove
        );
      } else {
        const selectedService = findServiceForBusinessOption(services, option);
        const serviceId = selectedService?._id || selectedService?.id || null;
        const anchor = prev.businessDetails[0] || {};

        updatedBusinessType = [...prev.Business_type, option];
        updatedBusinessDetails = [
          ...prev.businessDetails,
          {
            service_id: serviceId,
            service_name: option,
            Business_name: "",
            state: anchor.state || "",
            city: anchor.city || "",
            Business_address: "",
            pincode: "",
            full_address: "",
            Contact_number: "",
            Licence: "",
            Year_Business: "",
            email: "",
            businessLicense: [],
            portfolio: [],
          },
        ];
      }

      return {
        ...prev,
        Business_type: updatedBusinessType,
        businessDetails: updatedBusinessDetails,
      };
    });
  };

  const handleBusinessLicenseUpload = (e, businessIndex) => {
    const newFiles = Array.from(e.target?.files || []).filter(
      (f) => f instanceof File
    );
    if (!newFiles.length) return;

    setSignUpdata((prev) => {
      const updatedDetails = [...prev.businessDetails];

      if (!updatedDetails[businessIndex]) {
        updatedDetails[businessIndex] = {};
      }

      const cur = updatedDetails[businessIndex];
      const existing = normalizeFileList(cur.businessLicense);

      updatedDetails[businessIndex] = {
        ...cur,
        businessLicense: [...existing, ...newFiles],
      };

      return { ...prev, businessDetails: updatedDetails };
    });

    setErrors((prev) => ({
      ...prev,
      [businessIndex]: {
        ...(prev[businessIndex] || {}),
        businessLicense: "",
      },
    }));

    e.target.value = "";
  };

  const handleFileChange = (e, field, businessIndex) => {
    const newFiles = Array.from(e.target?.files || []).filter(
      (f) => f instanceof File
    );
    if (!newFiles.length) return;

    setSignUpdata((prev) => {
      const updatedDetails = [...prev.businessDetails];

      if (!updatedDetails[businessIndex]) {
        updatedDetails[businessIndex] = {};
      }

      const cur = updatedDetails[businessIndex];
      const existing = normalizeFileList(cur[field]);

      updatedDetails[businessIndex] = {
        ...cur,
        [field]: [...existing, ...newFiles],
      };

      return { ...prev, businessDetails: updatedDetails };
    });

    setErrors((prev) => ({
      ...prev,
      [businessIndex]: {
        ...(prev[businessIndex] || {}),
        [field]: "",
      },
    }));

    e.target.value = "";
  };

  const getBackgroundColor = (businessIndex, field) => {
    const businessDetail = Signupdata.businessDetails[businessIndex] || {};
    return normalizeFileList(businessDetail[field]).length > 0
      ? "bg-buttonbg"
      : "bg-white";
  };

  const handleRegisterTypeSelect = (option) => {
    setSignUpdata((prev) => ({
      ...prev,
      Register_type: option,
    }));
    setIsRegisterTypeOpen(false);
  };

  const handleInputChange = (e, businessIndex) => {
    const { name, value } = e.target;

    if (name === "email") {
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!emailRegex.test(value)) {
        setEError((prev) => ({
          ...prev,
          [businessIndex]: "Please enter valid email.",
        }));
      } else {
        setEError((prev) => ({ ...prev, [businessIndex]: "" }));
      }
    }

    let processedValue = value;
    if (name === "Contact_number") {
      const numericValue = value.replace(/\D/g, "");
      processedValue = numericValue.slice(0, 10);
    } else if (name === "pincode") {
      processedValue = value.replace(/\D/g, "").slice(0, 6);
    } else if (name === "Business_name") {
      // Only allow letters and spaces, limit to 40 characters
      const lettersOnly = value.replace(/[^a-zA-Z\s]/g, "");
      processedValue = lettersOnly.slice(0, 40);
    } else if (name === "Licence") {
      // Alphanumeric + common punctuation for registration numbers (no stripping)
      processedValue = value.slice(0, 100);
    }

    setSignUpdata((prev) => {
      let updatedDetails = [...prev.businessDetails];
      while (updatedDetails.length <= businessIndex) {
        updatedDetails.push({});
      }
      const nextRow = {
        ...updatedDetails[businessIndex],
        [name]: processedValue,
      };
      if (name === "state") {
        nextRow.city = "";
      }
      updatedDetails[businessIndex] = nextRow;
      return { ...prev, businessDetails: updatedDetails };
    });

    setErrors((prev) => {
      const updatedErrors = { ...prev };
      if (updatedErrors[businessIndex]) {
        updatedErrors[businessIndex] = {
          ...updatedErrors[businessIndex],
          [name]: "",
        };
      }
      return updatedErrors;
    });
  };

  const validateCurrentBusiness = () => {
    const businessDetail =
      Signupdata.businessDetails[currentBusinessIndex] || {};
    const businessErrors = {};
    let isValid = true;
    const isServiceFlow = Signupdata.Register_type === "Service";

    const bizName = safeStr(businessDetail.Business_name);
    if (!bizName) {
      businessErrors.Business_name = "Business name is required.";
      isValid = false;
    } else if (!/^[a-zA-Z\s]+$/.test(bizName)) {
      businessErrors.Business_name =
        "Business name should contain only letters.";
      isValid = false;
    } else if (bizName.length > 40) {
      businessErrors.Business_name =
        "Business name should not exceed 40 characters.";
      isValid = false;
    }
    /* State/city are collected only on step 1 for Service; synced to all rows. Rent/Shop enter here. */
    if (!isServiceFlow) {
      if (!safeStr(businessDetail.state)) {
        businessErrors.state = "State is required.";
        isValid = false;
      }
      if (!safeStr(businessDetail.city)) {
        businessErrors.city = "City is required.";
        isValid = false;
      }
    }
    if (!safeStr(businessDetail.Business_address)) {
      businessErrors.Business_address = "Business address is required.";
      isValid = false;
    }
    const pin = safeStr(businessDetail.pincode);
    if (!pin) {
      businessErrors.pincode = "Pincode is required.";
      isValid = false;
    } else if (!/^\d{6}$/.test(pin)) {
      businessErrors.pincode = "Enter a valid 6-digit pincode.";
      isValid = false;
    }
    if (!safeStr(businessDetail.full_address)) {
      businessErrors.full_address = "Full address is required.";
      isValid = false;
    }
    const contactPhone = safeStr(businessDetail.Contact_number);
    if (!contactPhone) {
      businessErrors.Contact_number = "Contact number is required.";
      isValid = false;
    } else if (!/^\d{10}$/.test(contactPhone)) {
      businessErrors.Contact_number = "Enter a valid 10-digit number.";
      isValid = false;
    }
    if (!safeStr(businessDetail.Licence)) {
      businessErrors.Licence = "License/Registration number is required.";
      isValid = false;
    }
    if (!safeStr(businessDetail.Year_Business)) {
      businessErrors.Year_Business = "Years in business is required.";
      isValid = false;
    }
    const bizEmail = safeStr(businessDetail.email);
    if (!bizEmail) {
      businessErrors.email = "Email is required.";
      isValid = false;
    } else {
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!emailRegex.test(bizEmail)) {
        businessErrors.email = "Please enter valid email.";
        isValid = false;
      }
    }

    if (normalizeFileList(businessDetail.businessLicense).length === 0) {
      businessErrors.businessLicense =
        "Please upload at least one business license file.";
      isValid = false;
    }
    if (normalizeFileList(businessDetail.portfolio).length === 0) {
      businessErrors.portfolio =
        "Please upload at least one portfolio / address proof file.";
      isValid = false;
    }

    setErrors((prev) => ({
      ...prev,
      [currentBusinessIndex]: businessErrors,
    }));

    return isValid;
  };

  const handleNextBusiness = async () => {
    if (!validateCurrentBusiness()) {
      return;
    }

    setCompletedBusinesses((prev) => [...prev, currentBusinessIndex]);

    if (currentBusinessIndex < Signupdata.Business_type.length - 1) {
      setCurrentBusinessIndex((prev) => prev + 1);
    } else {
      await submitAllBusinesses();
    }
  };

  const buildRegisterVendorStep1FormData = (businessDetailsOverride) => {
    const signupData = Signupdata || {};
    const businessDetails =
      businessDetailsOverride ?? signupData.businessDetails ?? [];
    const registerType = signupData.Register_type || "";
    const firstBusinessDetail = businessDetails[0] || {};
    const serviceType = registerType.toLowerCase();

    const fd = new FormData();
    fd.append("step", "1");
    fd.append("name", signupData.vendorName || "");
    fd.append("email", signupData.vendorEmail || "");
    fd.append("phone", signupData.vendorPhone || "");
    fd.append("business_name", firstBusinessDetail.Business_name || "");
    fd.append("state", firstBusinessDetail.state || "");
    fd.append("city", firstBusinessDetail.city || "");
    fd.append("pincode", firstBusinessDetail.pincode || "");
    fd.append(
      "full_address",
      firstBusinessDetail.full_address ||
        firstBusinessDetail.Business_address ||
        ""
    );
    fd.append(
      "business_years",
      String(parseInt(firstBusinessDetail.Year_Business, 10) || 0)
    );
    fd.append("license_number", firstBusinessDetail.Licence || "");
    fd.append("service_type", serviceType);

    if (serviceType === "service") {
      const businessTypeArray = businessDetails.map((detail) => ({
        service_id: detail.service_id || "",
        business_name: detail.Business_name || "",
        state: detail.state || "",
        city: detail.city || "",
        business_address: detail.Business_address || "",
        pincode: detail.pincode || "",
        full_address:
          detail.full_address || detail.Business_address || "",
        business_email: detail.email || "",
        contact_number: detail.Contact_number || "",
        license_number: detail.Licence || "",
        experience: parseInt(detail.Year_Business, 10) || 0,
      }));
      fd.append("business_type", JSON.stringify(businessTypeArray));
    }

    const licenseFiles = businessDetails.flatMap((d) =>
      normalizeFileList(d?.businessLicense)
    );
    const addressFiles = businessDetails.flatMap((d) =>
      normalizeFileList(d?.portfolio)
    );

    licenseFiles.forEach((file) => fd.append("business_license", file));
    addressFiles.forEach((file) => fd.append("address_proof", file));

    return fd;
  };

  const submitAllBusinesses = async () => {
    setLoading(true);

    try {
      const registerType = (Signupdata.Register_type || "").toLowerCase();
      let businessDetailsForRequest = Signupdata.businessDetails || [];
      if (registerType === "service") {
        businessDetailsForRequest = hydrateServiceIdsOnDetails(
          Signupdata.businessDetails,
          Signupdata.Business_type,
          services
        );
        const missingRows = businessDetailsForRequest.filter((bt) => !bt.service_id);
        if (missingRows.length > 0) {
          const labels = missingRows.map(
            (bt) =>
              safeStr(bt.service_name) ||
              safeStr(bt.Business_name) ||
              "Unknown"
          );
          const sample = services
            .slice(0, 15)
            .map((s) => safeStr(s?.name) || safeStr(s?.title))
            .filter(Boolean)
            .join(", ");
          toast.error(
            `No matching service ID for: ${labels.join(
              ", "
            )}. Wait for business types to finish loading, then open Business type and select again. Examples from server: ${
              sample || "(none — check getServices API)"
            }.`
          );
          setLoading(false);
          return;
        }
      }

      const formDataPayload =
        buildRegisterVendorStep1FormData(businessDetailsForRequest);

      const response = await handleApiCall(
        "post",
        `${endpoint.registerVendor}`,
        formDataPayload
      );

      if (response?.response) {
        const errorMessage =
          response?.response?.data?.errorMessage ||
          response?.response?.data?.message ||
          response?.data?.message ||
          "Submission failed";
        console.log(errorMessage, "Error message");
        toast.error(errorMessage);
      } else if (response?.data?.code === 200 || response?.status === 200) {
        toast.success("Registration completed successfully!");

        const transformedBusinessDetails = businessDetailsForRequest.map(
          (detail) => {
            const { email, ...detailWithoutEmail } = detail;
            return {
              ...detailWithoutEmail,
              business_email: email || "",
            };
          }
        );

        const transformedSignupData = {
          ...Signupdata,
          email: Signupdata.vendorEmail || "",
          businessDetails: transformedBusinessDetails,
        };

        const { vendorEmail, ...signupDataWithoutVendorEmail } =
          transformedSignupData;
        navigate("/sign-up-vendor", {
          state: { Signupdata: signupDataWithoutVendorEmail },
        });
      } else {
        const errorMessage =
          response?.data?.errorMessage ||
          response?.data?.message ||
          "Submission failed";
        toast.error(errorMessage);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.errorMessage ||
          "An error occurred during submission"
      );
    } finally {
      setLoading(false);
    }
  };

  const handlePreviousBusiness = () => {
    if (currentBusinessIndex > 0) {
      setCurrentBusinessIndex((prev) => prev - 1);
    }
  };

  const validateStep1 = () => {
    const newErrors = {};
    let isValid = true;

    if (!Signupdata.vendorName?.trim()) {
      newErrors.vendorName = "Company name is required.";
      isValid = false;
    } else if (!/^[a-zA-Z\s]+$/.test(Signupdata.vendorName.trim())) {
      newErrors.vendorName =
        "Name should contain only letters.";
      isValid = false;
    } else if (Signupdata.vendorName.trim().length > 20) {
      newErrors.vendorName =
        "Name should not exceed 20 characters.";
      isValid = false;
    }

    if (!Signupdata.vendorPhone?.trim()) {
      newErrors.vendorPhone = "Contact number is required.";
      isValid = false;
    } else if (!/^\d{10}$/.test(Signupdata.vendorPhone.trim())) {
      newErrors.vendorPhone = "Enter a valid 10-digit number.";
      isValid = false;
    }

    if (!Signupdata.vendorEmail?.trim()) {
      newErrors.vendorEmail = "Email is required.";
      isValid = false;
    } else {
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!emailRegex.test(Signupdata.vendorEmail.trim())) {
        newErrors.vendorEmail = "Please enter valid email.";
        isValid = false;
      }
    }

    if (!Signupdata.Register_type) {
      newErrors.Register_type = "Please select service type.";
      isValid = false;
    }

    if (
      Signupdata.Register_type === "Service" &&
      (!Signupdata.Business_type ||
        Signupdata.Business_type.length < 3)
    ) {
      newErrors.Business_type =
        "Please select at least 3 business types.";
      isValid = false;
    }

    if (!agreeToTerms) {
      newErrors.agreeToTerms = "You must agree to the terms and conditions.";
      isValid = false;
    }

    const bd0 = Signupdata.businessDetails[0] || {};
    if (!safeStr(bd0.state)) {
      isValid = false;
    }
    if (!safeStr(bd0.city)) {
      isValid = false;
    }

    setVendorErrors(newErrors);
    setErrors((prev) => ({
      ...prev,
      0: {
        ...(prev[0] || {}),
        state: !safeStr(bd0.state) ? "State is required." : "",
        city: !safeStr(bd0.city) ? "City is required." : "",
      },
    }));
    return isValid;
  };

  const handleVendorInputChange = (e) => {
    const { name, value } = e.target;

    // For mobile number fields, only allow digits and limit to 10 digits
    if (name === "vendorPhone") {
      const numericValue = value.replace(/\D/g, ""); // Remove all non-digit characters
      const limitedValue = numericValue.slice(0, 10); // Limit to 10 digits

      setSignUpdata((prev) => ({
        ...prev,
        [name]: limitedValue,
      }));
    } else if (name === "vendorName") {
      // Only allow letters and spaces, limit to 20 characters
      const lettersOnly = value.replace(/[^a-zA-Z\s]/g, "");
      const limitedValue = lettersOnly.slice(0, 20);
      setSignUpdata((prev) => ({
        ...prev,
        [name]: limitedValue,
      }));
    } else {
      setSignUpdata((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    setVendorErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const handleFirstStepNext = () => {
    if (!validateStep1()) return;
    /* Copy step-1 state/city to every business row (Service multi-row uses same location). */
    setSignUpdata((prev) => {
      const st = safeStr(prev.businessDetails[0]?.state);
      const ct = safeStr(prev.businessDetails[0]?.city);
      const details = (prev.businessDetails || []).map((d) => ({
        ...d,
        state: st,
        city: ct,
      }));
      return { ...prev, businessDetails: details };
    });
    setCurrentStep(2);
  };

  const registerType = Signupdata?.Register_type;
  const isService = registerType === "Service";
  const isRentOrShop = registerType === "Rent" || registerType === "Shop";
  const currentBusinessDetail =
    Signupdata.businessDetails[currentBusinessIndex] || {};
  const currentBusinessErrors = errors[currentBusinessIndex] || {};

  const cityOptionsForCurrentBusiness = useMemo(() => {
    const st = currentBusinessDetail.state;
    if (!st) return [];
    const row = indianStatesAndCities.find((item) => item.state === st);
    return Array.isArray(row?.cities) ? row.cities : [];
  }, [currentBusinessDetail.state]);

  return (
    <>
      <div className="flex h-[100dvh] max-h-[100dvh] w-full flex-col overflow-hidden md:h-screen md:max-h-none md:flex-row">
        <div className="flex min-h-0 flex-1 flex-col md:h-full md:w-6/12">
          <div className="flex min-h-0 flex-1 justify-center px-4 py-4 sm:px-6 md:px-0 md:py-8">
            <div className="flex w-full max-w-md min-h-0 shrink-0 grow-0 basis-auto flex-col md:h-full md:max-w-none md:w-80">
              <div className="shrink-0">
                <img src="images/black-logo.svg" alt="" className="mx-auto" />

                <div className="py-2 text-center font-BonaNovaRegular text-[22px] text-[#000000] md:text-[27px]">
                  Register for
                </div>

                <img
                  src="/images/progress-start.png"
                  alt=""
                  className="mx-auto w-36 py-2 md:w-40"
                />
              </div>

              {/* Scrollable only for the form fields, not the action buttons */}
              <div
                ref={scrollableContainerRef}
                className="mt-3 min-h-0 flex-1 overflow-y-auto no-scrollbar md:mt-4 [&_input]:!text-base md:[&_input]:!text-[13px] [&_select]:!text-base md:[&_select]:!text-[13px] [&_textarea]:!text-base md:[&_textarea]:!text-[13px]"
                style={{
                  scrollbarWidth: "none", // Firefox
                  msOverflowStyle: "none", // IE and Edge
                }}
              >
                <form
                  onSubmit={(e) => e.preventDefault()}
                  className="space-y-3"
                >
                  {currentStep === 1 && (
                    <>
                      <div className="mb-[18px]">
                        <div className="text-[13px] font-Montserratmedium text-[#2B2B2B] opacity-50 mb-1">
                          Company name
                        </div>
                        <input
                          type="text"
                          name="vendorName"
                          placeholder="Company name"
                          value={Signupdata.vendorName}
                          onChange={handleVendorInputChange}
                          maxLength={20}
                          className={`w-full px-3 py-2 border ${
                            vendorErrors.vendorName
                              ? "border-red-500"
                              : "!border-inputfieldborder"
                          } bg-white outline-none text-13 font-Montserratmedium text-forgetpassword min-h-[42px]`}
                        />
                        {vendorErrors.vendorName && (
                          <p className="text-red-500 text-xs mt-1">
                            {vendorErrors.vendorName}
                          </p>
                        )}
                      </div>

                      <div className="mb-[18px]">
                        <div className="text-[13px] font-Montserratmedium text-[#2B2B2B] opacity-50 mb-1">
                          Contact number
                        </div>
                        <input
                          type="text"
                          name="vendorPhone"
                          placeholder="Contact number"
                          value={Signupdata.vendorPhone}
                          onChange={handleVendorInputChange}
                          maxLength={10}
                          pattern="[0-9]*"
                          inputMode="numeric"
                          className={`w-full px-3 py-2 border ${
                            vendorErrors.vendorPhone
                              ? "border-red-500"
                              : "!border-inputfieldborder"
                          } bg-white outline-none text-13 font-Montserratmedium text-forgetpassword min-h-[42px]`}
                        />
                        {vendorErrors.vendorPhone && (
                          <p className="text-red-500 text-xs mt-1">
                            {vendorErrors.vendorPhone}
                          </p>
                        )}
                      </div>

                      <div className="mb-[18px]">
                        <div className="text-[13px] font-Montserratmedium text-[#2B2B2B] opacity-50 mb-1">
                          Email id
                        </div>
                        <input
                          type="email"
                          name="vendorEmail"
                          placeholder="Email id"
                          value={Signupdata.vendorEmail}
                          onChange={handleVendorInputChange}
                          className={`w-full px-3 py-2 border ${
                            vendorErrors.vendorEmail
                              ? "border-red-500"
                              : "!border-inputfieldborder"
                          } bg-white outline-none text-13 font-Montserratmedium text-forgetpassword min-h-[42px]`}
                        />
                        {vendorErrors.vendorEmail && (
                          <p className="text-red-500 text-xs mt-1">
                            {vendorErrors.vendorEmail}
                          </p>
                        )}
                      </div>

                      <div className="mb-[18px]">
                        <div className="text-[13px] font-Montserratmedium text-[#2B2B2B] opacity-50 mb-1">
                          State
                        </div>
                        <div className="relative">
                          <select
                            name="state"
                            value={currentBusinessDetail.state || ""}
                            onChange={(e) =>
                              handleInputChange(e, currentBusinessIndex)
                            }
                            className={`w-full cursor-pointer appearance-none px-3 py-2 pr-9 border bg-white outline-none text-13 font-Montserratmedium text-forgetpassword min-h-[42px] ${
                              currentBusinessErrors.state
                                ? "border-red-500"
                                : "!border-inputfieldborder"
                            }`}
                          >
                            <option value="">Select state</option>
                            {indianStatesAndCities.map((row) => (
                              <option key={row.state} value={row.state}>
                                {row.state}
                              </option>
                            ))}
                          </select>
                          <img
                            src="/images/arrow-down.png"
                            alt=""
                            className="pointer-events-none absolute right-3 top-1/2 h-2 w-2 -translate-y-1/2"
                          />
                        </div>
                        {currentBusinessErrors.state && (
                          <p className="text-red-500 text-xs mt-1">
                            {currentBusinessErrors.state}
                          </p>
                        )}
                      </div>

                      <div className="mb-[18px]">
                        <div className="text-[13px] font-Montserratmedium text-[#2B2B2B] opacity-50 mb-1">
                          City
                        </div>
                        <div className="relative">
                          <select
                            name="city"
                            value={currentBusinessDetail.city || ""}
                            onChange={(e) =>
                              handleInputChange(e, currentBusinessIndex)
                            }
                            disabled={!currentBusinessDetail.state}
                            className={`w-full cursor-pointer appearance-none px-3 py-2 pr-9 border bg-white outline-none text-13 font-Montserratmedium text-forgetpassword min-h-[42px] disabled:cursor-not-allowed disabled:bg-gray-50 disabled:opacity-70 ${
                              currentBusinessErrors.city
                                ? "border-red-500"
                                : "!border-inputfieldborder"
                            }`}
                          >
                            <option value="">
                              {currentBusinessDetail.state
                                ? "Select city"
                                : "Select state first"}
                            </option>
                            {cityOptionsForCurrentBusiness.map((cityName) => (
                              <option key={cityName} value={cityName}>
                                {cityName}
                              </option>
                            ))}
                          </select>
                          <img
                            src="/images/arrow-down.png"
                            alt=""
                            className="pointer-events-none absolute right-3 top-1/2 h-2 w-2 -translate-y-1/2"
                          />
                        </div>
                        {currentBusinessErrors.city && (
                          <p className="text-red-500 text-xs mt-1">
                            {currentBusinessErrors.city}
                          </p>
                        )}
                      </div>
                      <div
                        className="relative w-full register-type-dropdown"
                        ref={registerTypeDropdownRef}
                      >
                        <div className="text-[13px] font-Montserratmedium text-[#2B2B2B] opacity-50 mb-[5px]">
                          Service type
                        </div>
                        <div
                          className={`cursor-pointer mt-1 w-full px-3 py-2 border ${
                            vendorErrors.Register_type
                              ? "border-red-500"
                              : "!border-inputfieldborder"
                          } bg-white outline-none text-[13px] !font-MontserratMedium !text-forgetpassword flex justify-between items-center min-h-[42px]`}
                          onClick={() =>
                            setIsRegisterTypeOpen(!isRegisterTypeOpen)
                          }
                        >
                          <span className="text-[13px] font-Montserratmedium text-[#2B2B2B] !text-forgetpassword">
                            {Signupdata.Register_type || "Select Register type"}
                          </span>
                          <img
                            src="/images/arrow-down.png"
                            alt="Arrow Down"
                            className="h-2 w-2"
                          />
                        </div>

                        {isRegisterTypeOpen && (
                          <div className="absolute left-0 mt-1 w-full bg-white border border-subhead rounded-sm shadow-lg z-10">
                            <ul className="space-y-2 p-2">
                              {registerTypeOptions.map((option, index) => (
                                <li
                                  key={index}
                                  className="px-2 py-1 hover:bg-gray-100 cursor-pointer flex items-center justify-between text-xs font-Montserratmedium"
                                  onClick={() =>
                                    handleRegisterTypeSelect(option)
                                  }
                                >
                                  <span>{option}</span>
                                  <div
                                    className={`w-4 h-4 border-2 ${
                                      Signupdata.Register_type === option
                                        ? "border-orange-500 bg-orange-500"
                                        : "border-orange-500"
                                    } flex items-center justify-center`}
                                  >
                                    {Signupdata.Register_type === option && (
                                      <svg
                                        className="w-3 h-3 text-white"
                                        fill="currentColor"
                                        viewBox="0 0 20 20"
                                      >
                                        <path
                                          fillRule="evenodd"
                                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                          clipRule="evenodd"
                                        />
                                      </svg>
                                    )}
                                  </div>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {vendorErrors.Register_type && (
                          <p className="text-red-500 text-xs mt-1">
                            {vendorErrors.Register_type}
                          </p>
                        )}
                      </div>

                      {isService && (
                        <div
                          className="relative w-full business-type-dropdown"
                          ref={businessTypeDropdownRef}
                        >
                          <div className="text-[13px] font-Montserratmedium text-[#2B2B2B] mb-[5px]">
                            Business type
                          </div>
                          <div
                            className={`cursor-pointer mt-1 w-full px-3 py-2 border ${
                              vendorErrors.Business_type
                                ? "border-red-500"
                                : "!border-inputfieldborder"
                            } bg-white outline-none text-[13px] !font-MontserratMedium !text-forgetpassword flex justify-between items-center min-h-[42px]`}
                            onClick={() => setIsOpens(!isOpens)}
                          >
                            <span className="text-[13px] font-Montserratmedium text-[#2B2B2B] !text-forgetpassword">
                              {Signupdata.Business_type.length > 0
                                ? `${Signupdata.Business_type.length} selected`
                                : "Select Business type"}
                            </span>
                            <img
                              src="/images/arrow-down.png"
                              alt="Arrow Down"
                              className="h-2 w-2"
                            />
                          </div>

                          {isOpens && (
                            <div className="relative z-20 mt-1 w-full overflow-hidden rounded-sm border border-subhead bg-white shadow-md">
                              {loadingServices ? (
                                <div className="p-4 text-center text-sm text-forgetpassword">
                                  Loading...
                                </div>
                              ) : businessTypeOptions.length === 0 ? (
                                <div className="p-4 text-center text-sm text-forgetpassword">
                                  No business types available
                                </div>
                              ) : (
                                <div
                                  className="h-44 overflow-y-auto p-2 custom-scrollbar"
                                  role="listbox"
                                  aria-label="Business types"
                                  aria-multiselectable="true"
                                >
                                  <ul className="space-y-0.5">
                                    {businessTypeOptions.map((option, index) => (
                                      <li
                                        key={index}
                                        role="option"
                                        aria-selected={Signupdata.Business_type.includes(
                                          option
                                        )}
                                        className="flex min-h-[2.25rem] cursor-pointer items-center justify-between px-2 py-2 text-xs font-Montserratmedium hover:bg-gray-100"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleBusinessTypeCheckbox(option);
                                        }}
                                      >
                                        <span>{option}</span>
                                        <div
                                          className={`flex h-4 w-4 shrink-0 items-center justify-center border-2 ${
                                            Signupdata.Business_type.includes(
                                              option
                                            )
                                              ? "border-orange-500 bg-orange-500"
                                              : "border-orange-500 bg-white"
                                          }`}
                                        >
                                          {Signupdata.Business_type.includes(
                                            option
                                          ) && (
                                            <svg
                                              className="h-3 w-3 text-white"
                                              fill="currentColor"
                                              viewBox="0 0 20 20"
                                            >
                                              <path
                                                fillRule="evenodd"
                                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                clipRule="evenodd"
                                              />
                                            </svg>
                                          )}
                                        </div>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>
                          )}

                          {vendorErrors.Business_type && (
                            <p className="text-red-500 text-xs mt-1">
                              {vendorErrors.Business_type}
                            </p>
                          )}
                        </div>
                      )}
                    </>
                  )}

                  {currentStep === 2 &&
                  ((isService && Signupdata.Business_type.length >= 3) ||
                    isRentOrShop) ? (
                    <div>
                      <div className="mb-[8px] text-[13px] font-normal leading-[16px] text-[#2B2B2B] font-Montserratbold">
                        {isService
                          ? `Service ${currentBusinessIndex + 1} Details`
                          : "Business Details"}
                      </div>

                      <div className="mb-[18px]">
                        <div className="text-[13px] font-Montserratmedium text-[#2B2B2B] opacity-50 mb-1">
                          Business name
                        </div>
                        <input
                          type="text"
                          name="Business_name"
                          placeholder="Business name"
                          required
                          value={currentBusinessDetail.Business_name || ""}
                          onChange={(e) =>
                            handleInputChange(e, currentBusinessIndex)
                          }
                          maxLength={40}
                          className={`w-full px-3 py-2 border ${
                            currentBusinessErrors.Business_name
                              ? "border-red-500"
                              : "!border-inputfieldborder"
                          } bg-white outline-none text-13 font-Montserratmedium text-forgetpassword min-h-[42px]`}
                        />
                        {currentBusinessErrors.Business_name && (
                          <p className="text-red-500 text-xs mt-1">
                            {currentBusinessErrors.Business_name}
                          </p>
                        )}
                      </div>


                      <div className="mb-[18px]">
                        <div className="text-[13px] font-Montserratmedium text-[#2B2B2B] opacity-50 mb-1">
                          Business address
                        </div>
                        <input
                          type="text"
                          name="Business_address"
                          placeholder="Business address"
                          required
                          value={currentBusinessDetail.Business_address || ""}
                          onChange={(e) =>
                            handleInputChange(e, currentBusinessIndex)
                          }
                          className={`w-full px-3 py-2 border ${
                            currentBusinessErrors.Business_address
                              ? "border-red-500"
                              : "!border-inputfieldborder"
                          } bg-white outline-none text-13 font-Montserratmedium text-forgetpassword min-h-[42px]`}
                        />
                        {currentBusinessErrors.Business_address && (
                          <p className="text-red-500 text-xs mt-1">
                            {currentBusinessErrors.Business_address}
                          </p>
                        )}
                      </div>

                      <div className="mb-[18px]">
                        <div className="text-[13px] font-Montserratmedium text-[#2B2B2B] opacity-50 mb-1">
                          Pincode
                        </div>
                        <input
                          type="text"
                          name="pincode"
                          placeholder="Pincode"
                          required
                          inputMode="numeric"
                          maxLength={6}
                          value={currentBusinessDetail.pincode || ""}
                          onChange={(e) =>
                            handleInputChange(e, currentBusinessIndex)
                          }
                          className={`w-full px-3 py-2 border ${
                            currentBusinessErrors.pincode
                              ? "border-red-500"
                              : "!border-inputfieldborder"
                          } bg-white outline-none text-13 font-Montserratmedium text-forgetpassword min-h-[42px]`}
                        />
                        {currentBusinessErrors.pincode && (
                          <p className="text-red-500 text-xs mt-1">
                            {currentBusinessErrors.pincode}
                          </p>
                        )}
                      </div>

                      <div className="mb-[18px]">
                        <div className="text-[13px] font-Montserratmedium text-[#2B2B2B] opacity-50 mb-1">
                          Full address
                        </div>
                        <input
                          type="text"
                          name="full_address"
                          placeholder="Full address"
                          required
                          value={currentBusinessDetail.full_address || ""}
                          onChange={(e) =>
                            handleInputChange(e, currentBusinessIndex)
                          }
                          className={`w-full px-3 py-2 border ${
                            currentBusinessErrors.full_address
                              ? "border-red-500"
                              : "!border-inputfieldborder"
                          } bg-white outline-none text-13 font-Montserratmedium text-forgetpassword min-h-[42px]`}
                        />
                        {currentBusinessErrors.full_address && (
                          <p className="text-red-500 text-xs mt-1">
                            {currentBusinessErrors.full_address}
                          </p>
                        )}
                      </div>

                      <div className="mb-[18px]">
                        <div className="text-[13px] font-Montserratmedium text-[#2B2B2B] opacity-50 mb-1">
                          Contact number
                        </div>
                        <input
                          type="text"
                          name="Contact_number"
                          placeholder="Contact number"
                          required
                          value={currentBusinessDetail.Contact_number || ""}
                          onChange={(e) =>
                            handleInputChange(e, currentBusinessIndex)
                          }
                          maxLength={10}
                          pattern="[0-9]*"
                          inputMode="numeric"
                          className={`w-full px-3 py-2 border ${
                            currentBusinessErrors.Contact_number
                              ? "border-red-500"
                              : "!border-inputfieldborder"
                          } bg-white outline-none text-13 font-Montserratmedium text-forgetpassword min-h-[42px]`}
                        />
                        {currentBusinessErrors.Contact_number && (
                          <p className="text-red-500 text-xs mt-1">
                            {currentBusinessErrors.Contact_number}
                          </p>
                        )}
                      </div>

                      <div className="mb-[18px]">
                        <div className="text-[13px] font-Montserratmedium text-[#2B2B2B] opacity-50 mb-1">
                          Business email
                        </div>
                        <input
                          type="email"
                          name="email"
                          placeholder="Business Email"
                          required
                          value={currentBusinessDetail.email || ""}
                          onChange={(e) =>
                            handleInputChange(e, currentBusinessIndex)
                          }
                          className={`w-full px-3 py-2 border ${
                            currentBusinessErrors.email
                              ? "border-red-500"
                              : "!border-inputfieldborder"
                          } bg-white outline-none text-13 font-Montserratmedium text-forgetpassword min-h-[42px]`}
                        />
                        {eError[currentBusinessIndex] && (
                          <p className="text-red-500 text-xs mt-1">
                            {eError[currentBusinessIndex]}
                          </p>
                        )}
                        {currentBusinessErrors.email && (
                          <p className="text-red-500 text-xs mt-1">
                            {currentBusinessErrors.email}
                          </p>
                        )}
                      </div>

                      <div className="mb-[18px]">
                        <div className="text-[13px] font-Montserratmedium text-[#2B2B2B] opacity-50 mb-1">
                          License/Registration number
                        </div>
                        <input
                          type="text"
                          name="Licence"
                          inputMode="text"
                          autoComplete="off"
                          maxLength={100}
                          placeholder="e.g. ABC-12345 or 12AB34CD"
                          required
                          value={currentBusinessDetail.Licence || ""}
                          onChange={(e) =>
                            handleInputChange(e, currentBusinessIndex)
                          }
                          className={`w-full px-3 py-2 border ${
                            currentBusinessErrors.Licence
                              ? "border-red-500"
                              : "!border-inputfieldborder"
                          } bg-white outline-none text-13 font-Montserratmedium text-forgetpassword min-h-[42px]`}
                        />
                        {currentBusinessErrors.Licence && (
                          <p className="text-red-500 text-xs mt-1">
                            {currentBusinessErrors.Licence}
                          </p>
                        )}
                      </div>

                      <div className="mb-[18px]">
                        <div className="text-[13px] font-Montserratmedium text-[#2B2B2B] opacity-50 mb-1">
                          Years in business
                        </div>
                        <input
                          type="number"
                          name="Year_Business"
                          placeholder="Years in business"
                          max={9999}
                          required
                          value={currentBusinessDetail.Year_Business || ""}
                          onChange={(e) =>
                            handleInputChange(e, currentBusinessIndex)
                          }
                          className={`w-full px-3 py-2 border ${
                            currentBusinessErrors.Year_Business
                              ? "border-red-500"
                              : "!border-inputfieldborder"
                          } bg-white outline-none text-13 font-Montserratmedium text-forgetpassword min-h-[42px]`}
                        />
                        {currentBusinessErrors.Year_Business && (
                          <p className="text-red-500 text-xs mt-1">
                            {currentBusinessErrors.Year_Business}
                          </p>
                        )}
                      </div>

                      <div
                        className={`mb-[18px] border !border-dashed !border-inputfieldborder py-3 cursor-pointer text-center relative ${getBackgroundColor(
                          currentBusinessIndex,
                          "businessLicense"
                        )}`}
                      >
                        <input
                          type="file"
                          multiple
                          key={`businessLicense-${currentBusinessIndex}`}
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                          onChange={(e) =>
                            handleBusinessLicenseUpload(e, currentBusinessIndex)
                          }
                          accept="image/*, application/pdf"
                        />
                        <div className="flex flex-nowrap mx-auto w-7/12 justify-center items-start gap-2">
                          <img
                            src="/images/upload.png"
                            alt="Upload Icon"
                            className="h-3 w-3 mt-[2px]"
                          />
                          <p className="text-[13px] font-Montserratmedium text-[#2B2B2B] leading-tight">
                            Upload business license 
                          </p>
                        </div>
                       
                        {currentBusinessErrors.businessLicense && (
                          <p className="text-red-500 text-xs mt-1 relative z-[1]">
                            {currentBusinessErrors.businessLicense}
                          </p>
                        )}
                      </div>

                      <div
                        className={`mb-[18px] border !border-dashed !border-inputfieldborder py-3 cursor-pointer text-center relative ${getBackgroundColor(
                          currentBusinessIndex,
                          "portfolio"
                        )}`}
                      >
                        <input
                          type="file"
                          multiple
                          key={`portfolio-${currentBusinessIndex}`}
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                          onChange={(e) =>
                            handleFileChange(
                              e,
                              "portfolio",
                              currentBusinessIndex
                            )
                          }
                          accept="image/*, application/pdf"
                        />
                        <div className="flex flex-nowrap mx-auto w-8/12 justify-center items-center gap-2">
                          <img
                            src="/images/upload.png"
                            alt="Upload Icon"
                            className="h-3 w-3"
                          />
                          <p className="text-[13px] font-Montserratmedium text-[#2B2B2B] leading-tight">
                            Upload portfolio &amp; address proof 
                          </p>
                        </div>
                       
                        {currentBusinessErrors.portfolio && (
                          <p className="text-red-500 text-xs mt-1 relative z-[1]">
                            {currentBusinessErrors.portfolio}
                          </p>
                        )}
                      </div>
                    </div>
                  ) : null}
                </form>
              </div>

              {/* Fixed action buttons below the scrollable area */}
              <div className="mt-auto shrink-0 pb-[max(0.5rem,env(safe-area-inset-bottom))] pt-2">
              {currentStep === 1 && (
                <>
                  <div className="mt-[16px]">
                    <button
                      type="button"
                      onClick={handleFirstStepNext}
                      className="px-8 py-2 bg-buttonbg text-[16px] text-white focus:outline-none font-BonaNovabold w-full disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                  <div className="mt-[13px]">
                    <div className="flex items-start gap-2">
                      <input
                        type="checkbox"
                        id="terms-checkbox"
                        checked={agreeToTerms}
                        onChange={(e) => {
                          e.stopPropagation();
                          setAgreeToTerms(e.target.checked);
                          if (e.target.checked && vendorErrors.agreeToTerms) {
                            setVendorErrors((prev) => ({
                              ...prev,
                              agreeToTerms: "",
                            }));
                          }
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className={`w-4 h-4 min-w-[16px] min-h-[16px] border-2 ${
                          vendorErrors.agreeToTerms
                            ? "border-red-500"
                            : "border-[#E57339]"
                        } cursor-pointer appearance-none flex items-center justify-center relative z-10`}
                        style={{
                          backgroundColor: agreeToTerms ? "#E57339" : "white",
                          backgroundImage: agreeToTerms
                            ? `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 20 20'%3E%3Cpath fill='white' d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z'/%3E%3C/svg%3E")`
                            : "none",
                          backgroundSize: "contain",
                          backgroundRepeat: "no-repeat",
                          backgroundPosition: "center",
                          pointerEvents: "auto",
                        }}
                      />
                      <span className="text-[13px] font-Montserratmedium leading-snug text-[#2B2B2B]">
                        I agree to the{" "}
                        <span
                          className="cursor-pointer text-[#E57339] hover:underline"
                          onClick={(e) => {
                            e.stopPropagation();
                            setIsTermsModalOpen(true);
                          }}
                        >
                          terms and conditions
                        </span>
                      </span>
                    </div>
                    {vendorErrors.agreeToTerms && (
                      <p className="text-red-500 text-xs mt-1">
                        {vendorErrors.agreeToTerms}
                      </p>
                    )}
                  </div>
                </>
              )}

              {currentStep === 2 && (
                <div className="mt-2 flex gap-2">
                  {currentBusinessIndex > 0 && (
                    <button
                      type="button"
                      onClick={handlePreviousBusiness}
                      className="px-8 py-2 bg-gray-300 text-[16px] text-gray-700 focus:outline-none font-BonaNovabold w-1/2"
                    >
                      Previous
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={handleNextBusiness}
                    disabled={loading}
                    className={`px-8 py-2 bg-buttonbg text-[16px] text-white focus:outline-none font-BonaNovabold ${
                      currentBusinessIndex > 0 ? "w-1/2" : "w-full"
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {loading ? "Processing..." : "Next"}
                  </button>
                </div>
              )}

              <p className="mt-2 py-2 text-center text-sm font-Montserratmedium text-newuser">
                Already a user?{" "}
                <Link to="/login" className="cursor-pointer text-subhead">
                  {" "}
                  Login{" "}
                </Link>
              </p>
              </div>
            </div>
          </div>
        </div>
        <div className="hidden h-[100dvh] min-h-0 md:block md:h-screen md:w-6/12 md:max-h-none">
          <SignupSlider />
        </div>
      </div>

      {/* Terms and Conditions Modal */}
      {isTermsModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-md px-4 py-8"
          onClick={() => setIsTermsModalOpen(false)}
        >
          <div
            className="relative flex w-full max-w-2xl flex-col overflow-hidden rounded-sm bg-white shadow-[0_12px_48px_rgba(0,0,0,0.18)]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative shrink-0 px-6 pb-3 pt-6 sm:px-10 sm:pb-4 sm:pt-7">
              <h2 className="text-center font-BonaNovabold text-[20px] leading-tight text-black sm:text-[22px]">
                Terms &amp; Conditions
              </h2>
              <button
                type="button"
                onClick={() => setIsTermsModalOpen(false)}
                className="absolute right-3 top-3 flex h-8 w-8 shrink-0 items-center justify-center rounded-[4px] border-0 bg-[#FDECE7] text-[#F06543] transition-colors hover:bg-[#FAD6CC] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#F06543] sm:right-5 sm:top-4"
                aria-label="Close modal"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                  className="block"
                  aria-hidden
                >
                  <path
                    d="M1 1L13 13M13 1L1 13"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>

            <div className="max-h-[calc(90vh-8.5rem)] overflow-y-auto px-6 pb-6 pt-0 sm:px-10 sm:pb-8 sm:max-h-[calc(90vh-9rem)] custom-scrollbar">
              <div className="mx-auto max-w-4xl space-y-4 text-center text-sm font-Montserratregular leading-[1.65] text-[#666666] sm:space-y-5 sm:text-[15px]">
                <p>
                  Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed
                  diam nonumy eirmod tempor invidunt ut labore et dolore magna
                  aliquyam erat, sed diam voluptua. At vero eos et accusam et
                  justo duo dolores et ea rebum. Stet clita kasd gubergren, no
                  sea takimata sanctus est Lorem ipsum dolor sit amet.Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed
                  diam nonumy eirmod tempor invidunt ut labore et dolore magna
                  aliquyam erat, sed diam voluptua. At vero eos et accusam et
                  justo duo dolores et ea rebum. Stet clita kasd gubergren, no
                  sea takimata sanctus est Lorem ipsum dolor sit amet.
                  sea takimata sanctus est Lorem ipsum dolor sit amet.Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed  
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
</>
  );
};

export default Signup;
